// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export type xGoogleAnalyticsHandlerType = {
    includeAddToCartEvent?: boolean;
    includeUpdateCartEvent?: boolean;
    includePurchaseEvent?: boolean;
    includeRemoveFromCartLineEvent?: boolean;
    includeSelectPromotionEvent?: boolean;
    includeViewCartEvent?: boolean;
    includeMyAccountQuickLinks?: boolean;
    includeMyAccountRecentOrders?: boolean;
    includeMyAccountMyLists?: boolean;
    includeMyAccountRequisitions?: boolean;
    includeMyAccountOrderApproval?: boolean;
    includeMyAccountInvoiceHistory?: boolean;
    includePLPSearchEvent?: boolean;
    includePLPFilterEvent?: boolean;
    includePaymentPortalEvent?: boolean;
    includeShareProductEvent?: boolean;
    includeRMARequestEvent?: boolean;
    includeBMCancelProducts?: boolean;
    includeBMAlternateDC?: boolean;
    includeReorderEvent?: boolean;
    includeOrderHistoryEvent?: boolean;
    includeAutoDeliveryEvent?: boolean;
    includeScheduleReminderEvent?: boolean;
    includeLoginEvent?: boolean;
    includeRequestAccessEvent?: boolean;
    includeNavigationBarEvent?: boolean;
    includeToolBarNavigationEvent?: boolean;
    includeMyAccountShortcutEvent?: boolean;
    includePopularCategoriesEvent?: boolean;
    includeFeaturedItemsEvent?: boolean;
    includeFeaturedPartnersEvent?: boolean;
    includeOurSolutionsEvent?: boolean;
    includeSolutionsDeskBlogEvent?: boolean;
    includeMyAccountRecentOrdersHomePage?: boolean;
    includeHomePageSlider?: boolean;
    includeMyAccountFlyout?: boolean;
    includeMyOrderGuideCategory?: boolean;
    includeMyOrderGuideSearch?: boolean;
    includeSearchResult?: boolean;
    includeViewItemListEvent?: boolean;
    includeSelectItemListEvent?: boolean;
    includeAddToWishlistEvent?: boolean;
    includeAddQuickOrderEvent?: boolean;
    includeBeginCheckoutEvent?: boolean;
    includeCheckoutShippingInfoEvent?: boolean;
    includeCheckoutPaymentEvent?: boolean;
    includeViewPromotionEvent?: boolean;
    includeRegistrationEvent?: boolean;
    includePDPViewEvent?: boolean;
    includeCustomVirtualPageViewEvent?: boolean;
    includePunchOutCheckout?: boolean;
    includeCancelPunchOutCheckout?: boolean;
    includeNewAutoDeliveryEvent?: boolean;
};

export type xGoogleAnalyticsStateType = Readonly<xGoogleAnalyticsHandlerType>;

const initialGoogleAnalytics: xGoogleAnalyticsStateType = {
    includeAddToCartEvent: false,
    includeUpdateCartEvent: false,
    includePurchaseEvent: false,
    includeRemoveFromCartLineEvent: false,
    includeSelectPromotionEvent: false,
    includeViewCartEvent: false,
    includeMyAccountQuickLinks: false,
    includeMyAccountRecentOrders: false,
    includeMyAccountMyLists: false,
    includeMyAccountRequisitions: false,
    includeMyAccountOrderApproval: false,
    includeMyAccountInvoiceHistory: false,
    includePLPSearchEvent: false,
    includePLPFilterEvent: false,
    includePaymentPortalEvent: false,
    includeShareProductEvent: false,
    includeRMARequestEvent: false,
    includeBMCancelProducts: false,
    includeBMAlternateDC: false,
    includeReorderEvent: false,
    includeOrderHistoryEvent: false,
    includeAutoDeliveryEvent: false,
    includeScheduleReminderEvent: false,
    includeLoginEvent: false,
    includeRequestAccessEvent: false,
    includeNavigationBarEvent: false,
    includeToolBarNavigationEvent: false,
    includeMyAccountShortcutEvent: false,
    includePopularCategoriesEvent: false,
    includeFeaturedItemsEvent: false,
    includeFeaturedPartnersEvent: false,
    includeOurSolutionsEvent: false,
    includeSolutionsDeskBlogEvent: false,
    includeMyAccountRecentOrdersHomePage: false,
    includeHomePageSlider: false,
    includeMyAccountFlyout: false,
    includeMyOrderGuideCategory: false,
    includeMyOrderGuideSearch: false,
    includeSearchResult: false,
    includeViewItemListEvent: false,
    includeSelectItemListEvent: false,
    includeAddToWishlistEvent: false,
    includeAddQuickOrderEvent: false,
    includeBeginCheckoutEvent: false,
    includeCheckoutShippingInfoEvent: false,
    includeCheckoutPaymentEvent: false,
    includeViewPromotionEvent: false,
    includeRegistrationEvent: false,
    includePDPViewEvent: false,
    includeCustomVirtualPageViewEvent: false,
    includePunchOutCheckout: false,
    includeCancelPunchOutCheckout: false,
    includeNewAutoDeliveryEvent: false,
} as xGoogleAnalyticsStateType;

const reducer = {
    "xGoogleAnalytics/update": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { data: xGoogleAnalyticsHandlerType },
    ) => {
        draft["includeAddToCartEvent"] = action.data.includeAddToCartEvent ?? false;
        draft["includeUpdateCartEvent"] = action.data.includeUpdateCartEvent ?? false;
        draft["includePurchaseEvent"] = action.data.includePurchaseEvent ?? false;
        draft["includeRemoveFromCartLineEvent"] = action.data.includeRemoveFromCartLineEvent ?? false;
        draft["includeSelectPromotionEvent"] = action.data.includeSelectPromotionEvent ?? false;
        draft["includeViewCartEvent"] = action.data.includeViewCartEvent ?? false;
        draft["includeMyAccountQuickLinks"] = action.data.includeMyAccountQuickLinks ?? false;
        draft["includeMyAccountRecentOrders"] = action.data.includeMyAccountRecentOrders ?? false;
        draft["includeMyAccountMyLists"] = action.data.includeMyAccountMyLists ?? false;
        draft["includeMyAccountRequisitions"] = action.data.includeMyAccountRequisitions ?? false;
        draft["includeMyAccountOrderApproval"] = action.data.includeMyAccountOrderApproval ?? false;
        draft["includeMyAccountInvoiceHistory"] = action.data.includeMyAccountInvoiceHistory ?? false;
        draft["includePLPSearchEvent"] = action.data.includePLPSearchEvent ?? false;
        draft["includePLPFilterEvent"] = action.data.includePLPFilterEvent ?? false;
        draft["includePaymentPortalEvent"] = action.data.includePaymentPortalEvent ?? false;
        draft["includeShareProductEvent"] = action.data.includeShareProductEvent ?? false;
        draft["includeRMARequestEvent"] = action.data.includeRMARequestEvent ?? false;
        draft["includeBMCancelProducts"] = action.data.includeBMCancelProducts ?? false;
        draft["includeBMAlternateDC"] = action.data.includeBMAlternateDC ?? false;
        draft["includeReorderEvent"] = action.data.includeReorderEvent ?? false;
        draft["includeOrderHistoryEvent"] = action.data.includeOrderHistoryEvent ?? false;
        draft["includeAutoDeliveryEvent"] = action.data.includeAutoDeliveryEvent ?? false;
        draft["includeScheduleReminderEvent"] = action.data.includeScheduleReminderEvent ?? false;
        draft["includeLoginEvent"] = action.data.includeLoginEvent ?? false;
        draft["includeRequestAccessEvent"] = action.data.includeRequestAccessEvent ?? false;
        draft["includeNavigationBarEvent"] = action.data.includeNavigationBarEvent ?? false;
        draft["includeToolBarNavigationEvent"] = action.data.includeToolBarNavigationEvent ?? false;
        draft["includeMyAccountShortcutEvent"] = action.data.includeMyAccountShortcutEvent ?? false;
        draft["includePopularCategoriesEvent"] = action.data.includePopularCategoriesEvent ?? false;
        draft["includeFeaturedItemsEvent"] = action.data.includeFeaturedItemsEvent ?? false;
        draft["includeFeaturedPartnersEvent"] = action.data.includeFeaturedPartnersEvent ?? false;
        draft["includeOurSolutionsEvent"] = action.data.includeOurSolutionsEvent ?? false;
        draft["includeSolutionsDeskBlogEvent"] = action.data.includeSolutionsDeskBlogEvent ?? false;
        draft["includeMyAccountRecentOrdersHomePage"] = action.data.includeMyAccountRecentOrdersHomePage ?? false;
        draft["includeHomePageSlider"] = action.data.includeHomePageSlider ?? false;
        draft["includeMyAccountFlyout"] = action.data.includeMyAccountFlyout ?? false;
        draft["includeMyOrderGuideCategory"] = action.data.includeMyOrderGuideCategory ?? false;
        draft["includeMyOrderGuideSearch"] = action.data.includeMyOrderGuideSearch ?? false;
        draft["includeSearchResult"] = action.data.includeSearchResult ?? false;
        draft["includeViewItemListEvent"] = action.data.includeViewItemListEvent ?? false;
        draft["includeSelectItemListEvent"] = action.data.includeSelectItemListEvent ?? false;
        draft["includeAddToWishlistEvent"] = action.data.includeAddToWishlistEvent ?? false;
        draft["includeAddQuickOrderEvent"] = action.data.includeAddQuickOrderEvent ?? false;
        draft["includeBeginCheckoutEvent"] = action.data.includeBeginCheckoutEvent ?? false;
        draft["includeCheckoutShippingInfoEvent"] = action.data.includeCheckoutShippingInfoEvent ?? false;
        draft["includeCheckoutPaymentEvent"] = action.data.includeCheckoutPaymentEvent ?? false;
        draft["includeViewPromotionEvent"] = action.data.includeViewPromotionEvent ?? false;
        draft["includeRegistrationEvent"] = action.data.includeRegistrationEvent ?? false;
        draft["includePDPViewEvent"] = action.data.includePDPViewEvent ?? false;
        draft["includeCustomVirtualPageViewEvent"] = action.data.includeCustomVirtualPageViewEvent ?? false;
        draft["includePunchOutCheckout"] = action.data.includePunchOutCheckout ?? false;
        draft["includeCancelPunchOutCheckout"] = action.data.includeCancelPunchOutCheckout ?? false;
        draft["includeNewAutoDeliveryEvent"] = action.data.includeNewAutoDeliveryEvent ?? false;
    },
};

const xGoogleAnalytics = createTypedReducerWithImmer(initialGoogleAnalytics, reducer);

(reducers as any).xGoogleAnalytics = xGoogleAnalytics;

export type xGoogleAnalyticsState = {
    xGoogleAnalytics: xGoogleAnalyticsStateType;
} & ApplicationState;

type xGoogleAnalyticsReducers = {
    [key: string]: typeof xGoogleAnalytics;
};

export type xGoogleAnalyticsActions =
    | Parameters<xGoogleAnalyticsReducers[keyof xGoogleAnalyticsReducers]>[1]
    | AnyAction;
